import { Capacitor } from "@capacitor/core";

/** Parse CipherNest vault JSON / CSV into vault import items. */

export type ImportItem = {
  type?: number;
  name: string;
  notes?: string;
  folder?: string;
  favorite?: boolean;
  login?: {
    username?: string;
    password?: string;
    totp?: string;
    uris?: string[];
  };
  card?: Record<string, string>;
  identity?: Record<string, string>;
};

function parseCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i]!;
    if (inQ) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') inQ = false;
      else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") {
      out.push(cur);
      cur = "";
    } else cur += ch;
  }
  out.push(cur);
  return out;
}

export function parseImportFile(text: string, fileName: string): ImportItem[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  if (fileName.toLowerCase().endsWith(".json") || trimmed.startsWith("{")) {
    const data = JSON.parse(trimmed) as Record<string, unknown>;
    if (data.encrypted === true) {
      throw new Error(
        "Encrypted vault exports are not supported — export again without a password."
      );
    }
    const folders = (data.folders as Array<Record<string, unknown>>) || [];
    const folderById = new Map(
      folders.map((f) => [String(f.id), String(f.name || "")])
    );
    const items = (data.items as Array<Record<string, unknown>>) || [];
    if (!items.length) throw new Error("No items found in JSON export.");
    return items.map((it) => {
      const type = Number(it.type ?? 1);
      const folderId = it.folderId != null ? String(it.folderId) : "";
      const base: ImportItem = {
        type,
        name: String(it.name || "Imported"),
        notes: it.notes != null ? String(it.notes) : undefined,
        folder: folderById.get(folderId) || undefined,
        favorite: Boolean(it.favorite),
      };
      if (type === 1) {
        const login = (it.login as Record<string, unknown>) || {};
        const uris = ((login.uris as Array<Record<string, unknown>>) || [])
          .map((u) => String(u.uri || ""))
          .filter(Boolean);
        base.login = {
          username: login.username != null ? String(login.username) : "",
          password: login.password != null ? String(login.password) : "",
          totp: login.totp != null ? String(login.totp) : undefined,
          uris,
        };
      } else if (type === 3) {
        base.card = Object.fromEntries(
          Object.entries((it.card as Record<string, unknown>) || {}).map(
            ([k, v]) => [k, String(v ?? "")]
          )
        );
      } else if (type === 4) {
        base.identity = Object.fromEntries(
          Object.entries((it.identity as Record<string, unknown>) || {}).map(
            ([k, v]) => [k, String(v ?? "")]
          )
        );
      }
      return base;
    });
  }

  const lines = trimmed.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) throw new Error("CSV has no data rows.");
  const headers = parseCsvLine(lines[0]!).map((h) => h.trim().toLowerCase());
  const idx = (names: string[]) =>
    headers.findIndex((h) => names.includes(h));
  const iName = idx(["name", "title", "account"]);
  const iUser = idx(["username", "login_username", "login"]);
  const iPass = idx(["password", "login_password"]);
  const iUri = idx(["uri", "url", "login_uri", "website"]);
  const iTotp = idx(["totp", "otpauth", "login_totp"]);
  const iNotes = idx(["notes", "note", "comments"]);
  const iFolder = idx(["folder", "group", "collection"]);
  const iType = idx(["type"]);
  if (iName < 0) throw new Error("CSV needs a name column.");

  return lines.slice(1).map((line) => {
    const cols = parseCsvLine(line);
    const typeRaw = iType >= 0 ? (cols[iType] || "login").toLowerCase() : "login";
    const type =
      typeRaw.includes("note")
        ? 2
        : typeRaw.includes("card")
          ? 3
          : typeRaw.includes("identity")
            ? 4
            : 1;
    const item: ImportItem = {
      type,
      name: cols[iName] || "Imported",
      notes: iNotes >= 0 ? cols[iNotes] : undefined,
      folder: iFolder >= 0 ? cols[iFolder] : undefined,
    };
    if (type === 1) {
      item.login = {
        username: iUser >= 0 ? cols[iUser] : "",
        password: iPass >= 0 ? cols[iPass] : "",
        totp: iTotp >= 0 ? cols[iTotp] : undefined,
        uris: iUri >= 0 && cols[iUri] ? [cols[iUri]!] : [],
      };
    }
    return item;
  });
}

export async function downloadText(filename: string, text: string, mime: string) {
  if (Capacitor.isNativePlatform()) {
    try {
      const { Filesystem, Directory } = await import("@capacitor/filesystem");
      const { Share } = await import("@capacitor/share");
      const path = filename;
      await Filesystem.writeFile({
        path,
        data: btoa(unescape(encodeURIComponent(text))),
        directory: Directory.Cache,
      });
      const uri = await Filesystem.getUri({ path, directory: Directory.Cache });
      await Share.share({
        title: filename,
        url: uri.uri,
        dialogTitle: "Export vault",
      });
      return;
    } catch {
      /* fall through to browser download */
    }
  }
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
